// Profile View Component - User profile, analytics, and settings
import React, { useState, useEffect } from 'react';
import { User, Mail, Calendar, FileText, Edit2, LogOut, Award, TrendingUp, Users, BookOpen, Clock, Zap, MapPin, School, Briefcase, Phone, Github, Linkedin, Facebook, Globe, Twitter, Instagram, Share2, Flame, Eye, BarChart3 } from 'lucide-react';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import type { RoadmapData, ArticleData } from '../../types';
import { supabase } from '../../config/supabase';
import RoadmapStatsComponent from '../ui/RoadmapStatsComponent';
import { useAllRoadmapStats } from '../../hooks/useRoadmapStats';
import RoadmapStatsService from '../../services/roadmapStatsService';
import { getArticleById, recordToArticleData } from '../../services/articleService';
import { ArticleViewer } from './ArticleViewer';
import { LocalStorageSyncService } from '../../services/localStorageSyncService';

interface ProfileViewProps {
  user: SupabaseUser;
  roadmaps: RoadmapData[];
  theme: 'light' | 'dark';
  onSignOut: () => void;
  onBack: () => void;
  onArticleClick?: (roadmap: RoadmapData, monthIdx: number, weekIdx: number, dayIdx: number) => void;
  onViewArticle?: (article: ArticleData | null) => void;
}

export const ProfileView: React.FC<ProfileViewProps> = ({
  user,
  roadmaps,
  theme,
  onSignOut,
  onBack,
  onArticleClick,
  onViewArticle
}) => {
  const isDark = theme === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user.user_metadata?.full_name || '');
  const [viewedArticle, setViewedArticle] = useState<ArticleData | null>(null);
  const [institution, setInstitution] = useState('');
  const [location, setLocation] = useState('');
  const [major, setMajor] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [github, setGithub] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [facebook, setFacebook] = useState('');
  const [medium, setMedium] = useState('');
  const [twitter, setTwitter] = useState('');
  const [instagram, setInstagram] = useState('');
  const [incompleteArticles, setIncompleteArticles] = useState<Array<{
    id: string;
    title: string;
    roadmapTitle: string;
    roadmapId: string;
  }>>([]);

  // Load profile data on component mount
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        console.group('📋 Loading Profile Data');
        console.log('User Email:', user.email);
        console.log('Attempting Supabase query...');
        
        // First try to load from Supabase - use maybeSingle() to avoid errors when no profile exists
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('email', user.email)
          .maybeSingle(); // Returns null instead of error when no rows found

        // Only throw on actual database errors, not "no rows found"
        if (error) {
          console.error('❌ Supabase database error:', error);
          throw error;
        }

        if (data) {
          // Load from Supabase
          setDisplayName(data.display_name || user.user_metadata?.full_name || '');
          setInstitution(data.institution || '');
          setLocation(data.location || '');
          setMajor(data.major || '');
          setGender(data.gender || '');
          setAge(data.age || '');
          setPhoneNumber(data.phone_number || '');
          setGithub(data.github || '');
          setLinkedin(data.linkedin || '');
          setFacebook(data.facebook || '');
          setMedium(data.medium || '');
          setTwitter(data.twitter || '');
          setInstagram(data.instagram || '');
          console.log('✅ Profile data loaded from Supabase database');
        } else {
          console.log('ℹ️ No profile found in Supabase, checking localStorage');
          // Fallback to localStorage if no data found
          const savedProfile = localStorage.getItem(`user_profile_${user.email}`);
          if (savedProfile) {
            const profile = JSON.parse(savedProfile);
            setDisplayName(profile.displayName || user.user_metadata?.full_name || '');
            setInstitution(profile.institution || '');
            setLocation(profile.location || '');
            setMajor(profile.major || '');
            setGender(profile.gender || '');
            setAge(profile.age || '');
            setPhoneNumber(profile.phoneNumber || '');
            setGithub(profile.github || '');
            setLinkedin(profile.linkedin || '');
            setFacebook(profile.facebook || '');
            setMedium(profile.medium || '');
            setTwitter(profile.twitter || '');
            setInstagram(profile.instagram || '');
            console.log('✅ Profile data loaded from local storage (fallback)');
          } else {
            console.log('ℹ️ No profile data found, using defaults (first time user)');
          }
        }
        console.groupEnd();
      } catch (error) {
        console.error('❌ Error loading profile:', error);
        console.log('Falling back to localStorage...');
        
        // Fallback to localStorage on any error
        try {
          const savedProfile = localStorage.getItem(`user_profile_${user.email}`);
          if (savedProfile) {
            const profile = JSON.parse(savedProfile);
            setDisplayName(profile.displayName || user.user_metadata?.full_name || '');
            setInstitution(profile.institution || '');
            setLocation(profile.location || '');
            setMajor(profile.major || '');
            setGender(profile.gender || '');
            setAge(profile.age || '');
            setPhoneNumber(profile.phoneNumber || '');
            setGithub(profile.github || '');
            setLinkedin(profile.linkedin || '');
            setFacebook(profile.facebook || '');
            setMedium(profile.medium || '');
            setTwitter(profile.twitter || '');
            setInstagram(profile.instagram || '');
            console.log('✅ Profile data loaded from local storage (fallback)');
          }
        } catch (fallbackError) {
          console.error('Failed to load from localStorage:', fallbackError);
        }
        console.groupEnd();
      }
    };

    loadProfileData();
  }, [user.email]);

  // Calculate analytics
  const totalRoadmaps = roadmaps.length;
  const totalDays = roadmaps.reduce((acc, r) => {
    return acc + ((r.months || []).reduce((weekAcc, month) => {
      return weekAcc + ((month.weeks || []).reduce((dayAcc, week) => dayAcc + ((week.days || []).length), 0));
    }, 0));
  }, 0);
  const completedDays = roadmaps.reduce((acc, r) => acc + ((r.completedDays || []).length), 0);
  const completionPercentage = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0;
  const avgCompletionPerRoadmap = totalRoadmaps > 0 ? Math.round(completedDays / totalRoadmaps) : 0;

  const userJoinDate = user.created_at 
    ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Recently';

  const handleSaveProfile = async () => {
    // Save profile data to Supabase database
    const profileData = {
      email: user.email,
      display_name: displayName,
      institution,
      location,
      major,
      gender,
      age: age ? parseInt(age) : null,
      phone_number: phoneNumber,
      github,
      linkedin,
      facebook,
      medium,
      twitter,
      instagram,
      updated_at: new Date().toISOString()
    };

    try {
      console.group('💾 Saving Profile');
      console.log('User Email:', user.email);
      console.log('Profile data:', profileData);
      
      // Validate email matches current user (security check)
      if (profileData.email !== user.email) {
        throw new Error('Security Error: Email mismatch. Cannot save profile for different user.');
      }
      
      // Try to insert or update profile in Supabase
      console.log('📤 Sending upsert to Supabase...');
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert([profileData], { onConflict: 'email' });

      if (error) {
        console.error('❌ Supabase error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        throw error;
      }

      console.log('✅ Profile saved to Supabase database');
      
      // Also save to LocalStorageSyncService for offline access with 7-day auto-clear
      LocalStorageSyncService.saveUserProfile({
        email: user.email || '',
        displayName,
        avatar: user.user_metadata?.avatar_url,
        theme: isDark ? 'dark' : 'light',
        language: 'en',
        lastSynced: Date.now()
      });
      
      LocalStorageSyncService.savePreferences(user.email || '', {
        institution,
        location,
        major,
        gender,
        age,
        phoneNumber,
        github,
        linkedin,
        facebook,
        medium,
        twitter,
        instagram,
      });
      
      console.log('✅ Profile also synced to localStorage with auto-clear');
      console.groupEnd();
      
      setIsEditing(false);
      alert('Profile saved successfully!');
    } catch (error) {
      console.error('❌ Error saving profile:', error);
      console.groupEnd();
      
      // Fallback: still save to LocalStorageSyncService even if Supabase fails
      try {
        LocalStorageSyncService.saveUserProfile({
          email: user.email || '',
          displayName,
          avatar: user.user_metadata?.avatar_url,
          theme: isDark ? 'dark' : 'light',
          language: 'en',
          lastSynced: Date.now()
        });
        
        LocalStorageSyncService.savePreferences(user.email || '', {
          institution,
          location,
          major,
          gender,
          age,
          phoneNumber,
          github,
          linkedin,
          facebook,
          medium,
          twitter,
          instagram,
        });
        
      console.log('💾 Profile cached to localStorage as offline fallback');
        alert('Profile saved to device (offline). Will sync when online.');
      } catch (cacheError) {
        console.error('Failed to cache profile:', cacheError);
        alert('Error saving profile. Please try again.');
      }
    }
  };

  // Fetch incomplete articles from database
  useEffect(() => {
    const fetchIncompleteArticles = async () => {
      try {
        if (!roadmaps.length) {
          console.log('📚 No roadmaps found, skipping article fetch');
          setIncompleteArticles([]);
          return;
        }

        const roadmapIds = roadmaps.map((r: RoadmapData) => r.id).filter(Boolean);
        if (!roadmapIds.length) {
          console.log('📚 No valid roadmap IDs found');
          return;
        }

        console.log('📚 Fetching articles for roadmaps:', roadmapIds);

        // Get all articles for user's roadmaps
        const { data, error } = await supabase
          .from('articles')
          .select('id, title, roadmap_id, roadmaps(title)')
          .in('roadmap_id', roadmapIds)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('📚 Error fetching articles:', error);
          return;
        }

        if (!data || data.length === 0) {
          console.log('📚 No articles found for these roadmaps');
          setIncompleteArticles([]);
          return;
        }

        console.log('📚 Fetched articles from DB:', data.length);

        // Map articles for display
        const articles = data
          .slice(0, 5)
          .map((article: any) => ({
            id: article.id,
            title: article.title,
            roadmapTitle: article.roadmaps?.title || 'Unknown Roadmap',
            roadmapId: article.roadmap_id
          }));

        console.log('📚 Continue Reading articles:', articles.length);
        setIncompleteArticles(articles);
      } catch (error) {
        console.error('📚 Error in fetchIncompleteArticles:', error);
      }
    };

    fetchIncompleteArticles();
  }, [roadmaps, user?.id]);

  // Get stats for all roadmaps
  const { allStats, getTotalStudyTime, getTotalViews } = useAllRoadmapStats();

  // Helper to format total study time
  const formattedTotalTime = getTotalStudyTime();
  const displayTotalTime = RoadmapStatsService.formatTime(formattedTotalTime);

  // Mark article as completed when user closes it
  const handleArticleClose = async () => {
    if (viewedArticle && viewedArticle.id) {
      try {
        console.log('✅ Marking article as completed:', viewedArticle.id);
        
        // Check if progress record exists
        const { data: existing, error: queryError } = await supabase
          .from('user_article_progress')
          .select('id')
          .eq('email', user.email)
          .eq('article_id', viewedArticle.id)
          .maybeSingle();

        if (queryError) {
          console.error('❌ Query error:', queryError);
        }

        if (existing) {
          // Update existing record
          const { error: updateError } = await supabase
            .from('user_article_progress')
            .update({
              status: 'completed',
              progress_percentage: 100,
              last_accessed_at: new Date().toISOString(),
              completed_at: new Date().toISOString()
            })
            .eq('id', existing.id);
          
          if (updateError) {
            console.error('❌ Update error:', updateError);
          } else {
            console.log('✅ Article progress updated');
          }
        } else {
          // Create new record
          const { error: insertError } = await supabase
            .from('user_article_progress')
            .insert({
              email: user.email,
              user_id: user.id,
              article_id: viewedArticle.id,
              status: 'completed',
              progress_percentage: 100,
              last_accessed_at: new Date().toISOString(),
              completed_at: new Date().toISOString()
            });
          
          if (insertError) {
            console.error('❌ Insert error:', insertError);
          } else {
            console.log('✅ Article progress created');
          }
        }

        // Save to localStorage as well
        LocalStorageSyncService.saveArticleProgress({
          articleId: viewedArticle.id,
          completed: true,
          progress: 100,
          lastViewed: Date.now(),
          lastSynced: Date.now()
        });

        console.log('✅ Article marked as completed');
      } catch (error) {
        console.error('❌ Error marking article as completed:', error);
      }
    }
    
    // Close the article viewer
    setViewedArticle(null);
  };

  // If an article is being viewed, show the article viewer instead of profile
  if (viewedArticle) {
    return (
      <ArticleViewer
        article={viewedArticle}
        isDark={isDark}
        onBack={handleArticleClose}
        showDayBadge={false}
        user={user}
      />
    );
  }

  return (
    <div className="space-y-8 animate-in">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <h1 className={`text-4xl md:text-5xl font-black tracking-tight ${
          isDark ? 'text-white' : 'text-zinc-900'
        }`}>
          Profile
        </h1>
        <button
          onClick={onBack}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            isDark
              ? 'bg-zinc-800 hover:bg-zinc-700 text-white'
              : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-900'
          }`}
        >
          ← Back
        </button>
      </div>

      {/* Profile Card */}
      <div className={`rounded-3xl border p-8 transition-all ${
        isDark
          ? 'bg-gradient-to-br from-zinc-900/50 to-zinc-800/50 border-zinc-700'
          : 'bg-gradient-to-br from-white to-zinc-50 border-zinc-200'
      }`}>
        <div className="flex flex-col md:flex-row gap-8">
          {/* Profile Picture and Basic Info */}
          <div className="flex flex-col items-center md:items-start gap-6">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-red-500 via-rose-500 to-orange-500 text-white flex items-center justify-center text-5xl font-black shadow-xl shadow-red-500/30">
              {user.user_metadata?.avatar_url ? (
                <img src={user.user_metadata.avatar_url} alt={user.user_metadata?.full_name || 'Profile'} className="w-full h-full object-cover" />
              ) : (
                <span>{(user.user_metadata?.full_name || user.email || 'N')[0]?.toUpperCase()}</span>
              )}
            </div>

            {/* Basic Info */}
            <div className="w-full md:max-w-xs space-y-3">
              {!isEditing ? (
                <>
                  <div>
                    <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Name</p>
                    <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                      {user.user_metadata?.full_name || 'No name set'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Major</p>
                    <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                      {major || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Location</p>
                    <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                      {location || 'Not specified'}
                    </p>
                  </div>
                </>
              ) : null}

              <div className="flex gap-2 pt-2">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Profile
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSaveProfile}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setDisplayName(user.user_metadata?.full_name || '');
                      }}
                      className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                        isDark
                          ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                          : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-900'
                      }`}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Analytics - Right side */}
          <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { icon: BookOpen, label: 'Roadmaps', value: totalRoadmaps, color: 'red' },
              { icon: Clock, label: 'Days Logged', value: completedDays, color: 'orange' },
              { icon: TrendingUp, label: 'Completion', value: `${completionPercentage}%`, color: 'rose' },
              { icon: Award, label: 'Avg Days/Map', value: avgCompletionPerRoadmap, color: 'purple' },
              { icon: Zap, label: 'Total Days', value: totalDays, color: 'pink' },
              { icon: Users, label: 'Streak Days', value: 0, color: 'rose' }
            ].map((stat, idx) => {
              const Icon = stat.icon;
              const colorClasses = {
                red: isDark ? 'from-red-500/20 text-red-400' : 'from-red-100 text-red-600',
                orange: isDark ? 'from-orange-500/20 text-orange-400' : 'from-orange-100 text-orange-600',
                rose: isDark ? 'from-rose-500/20 text-rose-400' : 'from-rose-100 text-rose-600',
                purple: isDark ? 'from-purple-500/20 text-purple-400' : 'from-purple-100 text-purple-600',
                pink: isDark ? 'from-pink-500/20 text-pink-400' : 'from-pink-100 text-pink-600'
              };
              return (
                <div key={idx} className={`rounded-2xl p-4 bg-gradient-to-br ${colorClasses[stat.color as keyof typeof colorClasses]} flex flex-col items-center justify-center text-center`}>
                  <Icon className="w-6 h-6 mb-2" />
                  <p className={`text-2xl font-black mb-1`}>{stat.value}</p>
                  <p className="text-xs font-semibold opacity-75">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Personal Information Edit Section */}
      {isEditing && (
        <div className={`rounded-3xl border p-8 transition-all ${
          isDark
            ? 'bg-gradient-to-br from-zinc-900/50 to-zinc-800/50 border-zinc-700'
            : 'bg-gradient-to-br from-white to-zinc-50 border-zinc-200'
        }`}>
          <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            <Edit2 className="w-6 h-6 text-red-500" />
            Edit Personal Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Display Name */}
            <div>
              <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Full Name *
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your full name"
                className={`w-full px-4 py-2 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                }`}
              />
            </div>

            {/* Institution */}
            <div>
              <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Institution
              </label>
              <input
                type="text"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                placeholder="e.g., Stanford University"
                className={`w-full px-4 py-2 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                }`}
              />
            </div>

            {/* Major */}
            <div>
              <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Major/Field of Study
              </label>
              <input
                type="text"
                value={major}
                onChange={(e) => setMajor(e.target.value)}
                placeholder="e.g., Computer Science"
                className={`w-full px-4 py-2 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                }`}
              />
            </div>

            {/* Location */}
            <div>
              <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., London, United Kingdom"
                className={`w-full px-4 py-2 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                }`}
              />
            </div>

            {/* Gender */}
            <div>
              <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Gender
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                }`}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>

            {/* Age */}
            <div>
              <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Age
              </label>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="e.g., 25"
                min="13"
                max="120"
                className={`w-full px-4 py-2 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                }`}
              />
            </div>

            {/* Phone Number */}
            <div className="md:col-span-2">
              <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g., +1 (555) 123-4567"
                className={`w-full px-4 py-2 rounded-lg border transition-all ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                }`}
              />
            </div>
          </div>

          {/* Social Links Edit Section */}
          <div className={`pt-8 border-t ${isDark ? 'border-zinc-700' : 'border-zinc-200'}`}>
            <h3 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Share2 className="w-5 h-5 text-orange-500" />
              Social Links
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* GitHub */}
              <div>
                <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  GitHub
                </label>
                <input
                  type="url"
                  value={github}
                  onChange={(e) => setGithub(e.target.value)}
                  placeholder="https://github.com/username"
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${
                    isDark
                      ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  }`}
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={linkedin}
                  onChange={(e) => setLinkedin(e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${
                    isDark
                      ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  }`}
                />
              </div>

              {/* Facebook */}
              <div>
                <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Facebook
                </label>
                <input
                  type="url"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                  placeholder="https://facebook.com/username"
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${
                    isDark
                      ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  }`}
                />
              </div>

              {/* Medium */}
              <div>
                <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Medium
                </label>
                <input
                  type="url"
                  value={medium}
                  onChange={(e) => setMedium(e.target.value)}
                  placeholder="https://medium.com/@username"
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${
                    isDark
                      ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  }`}
                />
              </div>

              {/* Twitter */}
              <div>
                <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Twitter / X
                </label>
                <input
                  type="url"
                  value={twitter}
                  onChange={(e) => setTwitter(e.target.value)}
                  placeholder="https://twitter.com/username"
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${
                    isDark
                      ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  }`}
                />
              </div>

              {/* Instagram */}
              <div>
                <label className={`text-sm font-bold mb-2 block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Instagram
                </label>
                <input
                  type="url"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder="https://instagram.com/username"
                  className={`w-full px-4 py-2 rounded-lg border transition-all ${
                    isDark
                      ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : 'bg-white border-zinc-200 text-zinc-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Continue Reading Section */}
      <div className={`rounded-3xl border p-8 transition-all ${
        isDark
          ? 'bg-gradient-to-br from-zinc-900/50 to-zinc-800/50 border-zinc-700'
          : 'bg-gradient-to-br from-white to-zinc-50 border-zinc-200'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
          Continue Reading
        </h2>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {incompleteArticles.map((article) => (
            <button
              key={article.id}
              onClick={async () => {
                // Fetch full article data and view it
                console.log('🎯 Continue Reading clicked:', article.id, article.title);
                try {
                  const { data, error } = await getArticleById(article.id);
                  if (data && !error) {
                    console.log('📦 Raw article data from DB:', data);
                    const articleData = recordToArticleData(data);
                    console.log('✅ Converted article data:', articleData);
                    console.log('✅ Article fields:', {
                      id: articleData.id,
                      title: articleData.title,
                      deepDive: articleData.deepDive?.substring(0, 50),
                      concepts: articleData.technicalConcepts,
                      steps: articleData.steps,
                      example: articleData.example,
                      resources: articleData.resources
                    });
                    setViewedArticle(articleData);
                  } else {
                    console.error('Failed to fetch article:', error);
                  }
                } catch (e) {
                  console.error('Error fetching article:', e);
                }
              }}
              className={`w-full text-left p-4 rounded-lg border transition-all hover:shadow-md ${
                isDark ? 'border-zinc-600 bg-zinc-700/20 hover:bg-zinc-700/40' : 'border-zinc-200 bg-white hover:bg-zinc-100'
              }`}
            >
              <p className={`text-xs font-semibold text-blue-500 mb-1`}>
                {article.roadmapTitle}
              </p>
              <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                {article.title}
              </p>
            </button>
          ))}
          {incompleteArticles.length === 0 && (
            <p className={`text-sm text-center ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              All caught up! No pending articles.
            </p>
          )}
        </div>
      </div>

      {/* Personal Information */}
      <div className={`rounded-3xl border p-8 transition-all ${
        isDark
          ? 'bg-gradient-to-br from-zinc-900/50 to-zinc-800/50 border-zinc-700'
          : 'bg-gradient-to-br from-white to-zinc-50 border-zinc-200'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
          Personal Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Institution */}
          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <School className="w-5 h-5 text-red-500" />
              Institution
            </h3>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              {institution || 'Not specified'}
            </p>
          </div>

          {/* Location */}
          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <MapPin className="w-5 h-5 text-red-500" />
              Location
            </h3>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              {location || 'Not specified'}
            </p>
          </div>

          {/* Major */}
          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Briefcase className="w-5 h-5 text-orange-500" />
              Major/Field
            </h3>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              {major || 'Not specified'}
            </p>
          </div>

          {/* Gender */}
          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <User className="w-5 h-5 text-purple-500" />
              Gender
            </h3>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              {gender || 'Not specified'}
            </p>
          </div>

          {/* Age */}
          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Calendar className="w-5 h-5 text-pink-500" />
              Age
            </h3>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              {age || 'Not specified'}
            </p>
          </div>

          {/* Phone Number */}
          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Phone className="w-5 h-5 text-rose-500" />
              Phone Number
            </h3>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'} break-all`}>
              {phoneNumber || 'Not specified'}
            </p>
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className={`rounded-3xl border p-8 transition-all ${
        isDark
          ? 'bg-gradient-to-br from-zinc-900/50 to-zinc-800/50 border-zinc-700'
          : 'bg-gradient-to-br from-white to-zinc-50 border-zinc-200'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
          Social Links
        </h2>
        {!isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* GitHub */}
            <div className={`rounded-2xl p-6 border ${
              isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Github className="w-5 h-5 text-gray-600" />
                GitHub
              </h3>
              {github ? (
                <a href={github} target="_blank" rel="noopener noreferrer" className="text-sm text-red-500 hover:underline break-all font-semibold">
                  {github}
                </a>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Not specified</p>
              )}
            </div>

            {/* LinkedIn */}
            <div className={`rounded-2xl p-6 border ${
              isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Linkedin className="w-5 h-5 text-blue-600" />
                LinkedIn
              </h3>
              {linkedin ? (
                <a href={linkedin} target="_blank" rel="noopener noreferrer" className="text-sm text-red-500 hover:underline break-all font-semibold">
                  {linkedin}
                </a>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Not specified</p>
              )}
            </div>

            {/* Facebook */}
            <div className={`rounded-2xl p-6 border ${
              isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Facebook className="w-5 h-5 text-blue-500" />
                Facebook
              </h3>
              {facebook ? (
                <a href={facebook} target="_blank" rel="noopener noreferrer" className="text-sm text-red-500 hover:underline break-all font-semibold">
                  {facebook}
                </a>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Not specified</p>
              )}
            </div>

            {/* Medium */}
            <div className={`rounded-2xl p-6 border ${
              isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Globe className="w-5 h-5 text-emerald-600" />
                Medium
              </h3>
              {medium ? (
                <a href={medium} target="_blank" rel="noopener noreferrer" className="text-sm text-red-500 hover:underline break-all font-semibold">
                  {medium}
                </a>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Not specified</p>
              )}
            </div>

            {/* Twitter */}
            <div className={`rounded-2xl p-6 border ${
              isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Twitter className="w-5 h-5 text-sky-500" />
                Twitter / X
              </h3>
              {twitter ? (
                <a href={twitter} target="_blank" rel="noopener noreferrer" className="text-sm text-red-500 hover:underline break-all font-semibold">
                  {twitter}
                </a>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Not specified</p>
              )}
            </div>

            {/* Instagram */}
            <div className={`rounded-2xl p-6 border ${
              isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                <Instagram className="w-5 h-5 text-pink-600" />
                Instagram
              </h3>
              {instagram ? (
                <a href={instagram} target="_blank" rel="noopener noreferrer" className="text-sm text-red-500 hover:underline break-all font-semibold">
                  {instagram}
                </a>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Not specified</p>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Social & Connections */}
      <div className={`rounded-3xl border p-8 transition-all ${
        isDark
          ? 'bg-gradient-to-br from-zinc-900/50 to-zinc-800/50 border-zinc-700'
          : 'bg-gradient-to-br from-white to-zinc-50 border-zinc-200'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
          Social & Connections
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Users className="w-5 h-5 text-red-500" />
              Following
            </h3>
            <p className="text-3xl font-bold text-red-500 mb-2">0</p>
            <button className="text-sm text-red-500 hover:underline font-semibold">View all</button>
          </div>

          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Users className="w-5 h-5 text-purple-500" />
              Followers
            </h3>
            <p className="text-3xl font-bold text-purple-500 mb-2">0</p>
            <button className="text-sm text-purple-500 hover:underline font-semibold">View all</button>
          </div>

          <div className={`rounded-2xl p-6 border ${
            isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Award className="w-5 h-5 text-orange-500" />
              Achievements
            </h3>
            <p className="text-3xl font-bold text-orange-500 mb-2">3</p>
            <button className="text-sm text-orange-500 hover:underline font-semibold">View badges</button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className={`rounded-3xl border-2 border-red-500/30 p-8 transition-all ${
        isDark ? 'bg-red-500/5' : 'bg-red-50'
      }`}>
        <h2 className="text-2xl font-bold mb-6 text-red-600">Account Settings</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={onSignOut}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl font-bold hover:opacity-90 transition-all shadow-lg shadow-red-600/30"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};
// Helper component for insight cards
interface InsightCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  isDark: boolean;
}

const InsightCard: React.FC<InsightCardProps> = ({ icon, label, value, isDark }) => (
  <div className={`rounded-2xl p-6 border ${
    isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-200 bg-zinc-50'
  }`}>
    <div className="flex items-start justify-between mb-3">
      <h3 className={`font-semibold ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
        {label}
      </h3>
      <div className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
        {icon}
      </div>
    </div>
    <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
      {value}
    </p>
  </div>
);

export default ProfileView;
